import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query';
import { notificationsService } from '@/lib/api/services/notifications.service';

interface ListNotificationsParams {
  limit?: number;
  cursor?: string;
}

interface ListInAppParams {
  limit?: number;
  cursor?: string;
  unreadOnly?: boolean;
}

export function useNotifications(params: ListNotificationsParams = {}) {
  return useQuery({
    queryKey: queryKeys.notifications.list(params as Record<string, unknown>),
    queryFn: () => notificationsService.list(params),
  });
}

export function useJobNotifications(jobId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.notifications.list({ relatedEntityId: jobId }),
    queryFn: () =>
      notificationsService.list({ relatedEntityId: jobId!, limit: 50 }),
    enabled: !!jobId,
  });
}

export function useNotification(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.notifications.detail(id ?? ''),
    queryFn: () => notificationsService.get(id!),
    enabled: !!id,
  });
}

export function useSendNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      templateId: string;
      recipientEmails: string[];
      templateVars: Record<string, unknown>;
      relatedEntityType?: string;
      relatedEntityId?: string;
      manualSend?: boolean;
      ccRiskManagementDepartment?: boolean;
      ccEmails?: string[];
    }) => notificationsService.send(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.lists() });
    },
  });
}

export function useResendNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationsService.resend(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.lists() });
    },
  });
}

export function useInAppNotifications(params: ListInAppParams = {}) {
  return useQuery({
    queryKey: queryKeys.notifications.inApp(),
    queryFn: () => notificationsService.listInApp(params),
  });
}

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: () => notificationsService.getUnreadCount(),
    refetchInterval: 60000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { notificationId?: string; all?: boolean }) =>
      notificationsService.markAsRead(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.inApp() });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount() });
    },
  });
}
